import { getRoutine } from '../data/kivoTools';
import {
  getSkin,
  getHistory,
  listFavoriteProducts,
  searchProducts,
  getProductView,
  getIngredient,
  listIngredients,
  getJob,
  getPremiumJob,
  getHairAnalysisJob,
  getMakeupAnalysisJob,
} from '../api/endpoints';

import FaceScanScreen, { type FaceScanInput, type FaceScanResult } from '../screens/FaceScanScreen';
import PremiumFaceScanScreen, { type PremiumFaceScanInput, type PremiumFaceScanResult } from '../screens/PremiumFaceScanScreen';
import HairAnalysisScreen, { type HairAnalysisScreenInput, type HairAnalysisScreenResult } from '../screens/HairAnalysisScreen';
import MakeupAnalysisScreen, { type MakeupAnalysisScreenInput, type MakeupAnalysisScreenResult } from '../screens/MakeupAnalysisScreen';
import ProductCompareScreen, { type ProductCompareInput, type ProductCompareResult } from '../screens/ProductCompareScreen';
import ProductCompatScreen, { type ProductCompatInput, type ProductCompatResult } from '../screens/ProductCompatScreen';
import ProductSoloScreen, { type ProductSoloInput, type ProductSoloResult } from '../screens/ProductSoloScreen';
import WeatherScreen, { type WeatherInput, type WeatherResult } from '../screens/WeatherScreen';
import PreferencesScreen, { type PreferencesInput, type PreferencesResult } from '../screens/PreferencesScreen';

import type { ScreenComponent } from '../types/screen';
import type { Plan } from '../contexts/UserContext';
import type { JobsContextValue } from '../contexts/JobsContext';
import type { ModalHostContextValue } from '../contexts/ModalHostContext';
import { formatUnknownError } from './errorMessage';

export interface ToolParameters {
  type: 'object';
  properties: Record<string, { type: string; description: string; enum?: string[] }>;
  required: string[];
}

export interface ToolContext {
  plan: Plan;
  jobs: JobsContextValue;
  refreshPrefs: () => Promise<void>;
  modal: ModalHostContextValue;
}

export type ToolDef =
  | {
      name: string;
      description: string;
      parameters: ToolParameters;
      requiresPlan?: 'premium';
      interactive: false;
      handler: (args: Record<string, unknown>, ctx: ToolContext) => Promise<unknown>;
    }
  | {
      name: string;
      description: string;
      parameters: ToolParameters;
      requiresPlan?: 'premium';
      interactive: true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      screen: ScreenComponent<any, any>;
      // Optional shaper that turns the raw Screen result into a tool_result payload.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      shape?: (result: any, ctx: ToolContext, args: Record<string, unknown>) => unknown;
      // Optional pre-open hook. Useful for registering jobs in JobsContext.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onComplete?: (result: any, ctx: ToolContext, args: Record<string, unknown>) => void | Promise<void>;
    };

async function fetchSearchProducts(body: { product_keywords: string; limit?: number }): Promise<unknown[]> {
  const result: unknown = await searchProducts(body);
  if (!Array.isArray(result)) return [];
  return result as unknown[];
}

const argString = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (v == null) return '';
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
};
const argNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

export const TOOL_REGISTRY: ToolDef[] = [
  // ─── Scan results ─────────────────────────────────────────────────────────
  {
    name: 'analyze_skin',
    description: 'Fetch the photo insights for a completed scan job. Returns skin type, visible appearance patterns with confidence scores, personalised advice, and recommended base products. Use get_user_history first to find the job_id.',
    parameters: {
      type: 'object',
      properties: {
        job_id: { type: 'string', description: 'The completed scan job id (from get_user_history or after ai_face_scan).' },
      },
      required: ['job_id'],
    },
    interactive: false,
    handler: async (args) => {
      const id = argString(args.job_id);
      if (!id) return { error: 'job_id required' };
      try {
        return await getSkin(id);
      } catch (error: unknown) {
        return { error: formatUnknownError(error, 'Failed to fetch scan results') };
      }
    },
  },

  // ─── Product search ───────────────────────────────────────────────────────
  {
    name: 'get_products',
    description: 'Search the product database for recommendations matching a category and skin type.',
    parameters: {
      type: 'object',
      properties: {
        category: { type: 'string', description: 'Product category: cleanser, moisturizer, serum, sunscreen, toner, exfoliant, eye_cream, face_oil' },
        skin_type: { type: 'string', description: "The user's skin type", enum: ['oily', 'dry', 'combination', 'sensitive', 'normal'] },
        budget: { type: 'string', description: 'Budget preference', enum: ['budget', 'mid-range', 'luxury'] },
      },
      required: ['category', 'skin_type'],
    },
    interactive: false,
    handler: async (args) => {
      const category = argString(args.category);
      const skinType = argString(args.skin_type) || 'normal';
      const budget = argString(args.budget);
      const keywords = [category, skinType, 'skin', budget].filter(Boolean).join(' ');
      const items = await fetchSearchProducts({ product_keywords: keywords, limit: 5 });
      return { category, skin_type: skinType, budget: budget || null, recommendations: items };
    },
  },

  // ─── Routines (evidence-based, no backend equivalent) ────────────────────
  {
    name: 'get_routine',
    description: 'Generate a step-by-step skincare routine tailored to a skin type and time of day.',
    parameters: {
      type: 'object',
      properties: {
        skin_type: { type: 'string', description: "The user's skin type", enum: ['oily', 'dry', 'combination', 'sensitive', 'normal'] },
        time_of_day: { type: 'string', description: 'Which routine to generate', enum: ['morning', 'evening'] },
      },
      required: ['skin_type', 'time_of_day'],
    },
    interactive: false,
    handler: (args) => Promise.resolve(getRoutine(
      argString(args.skin_type) || 'normal',
      argString(args.time_of_day) || 'morning',
    )),
  },

  // ─── Ingredient lookup ────────────────────────────────────────────────────
  {
    name: 'check_ingredient',
    description: 'Look up benefits, concerns, compatibility, and pairing notes for a skincare ingredient from the database.',
    parameters: {
      type: 'object',
      properties: {
        ingredient: { type: 'string', description: 'Ingredient name (e.g. retinol, niacinamide, hyaluronic acid).' },
      },
      required: ['ingredient'],
    },
    interactive: false,
    handler: async (args) => {
      const name = argString(args.ingredient);
      if (!name) return { error: 'ingredient required' };
      const list = await listIngredients({ name, limit: 1 });
      if (list.items.length === 0) return { error: `No ingredient found for "${name}" in the database` };
      const first = list.items[0];
      return await getIngredient(first.id);
    },
  },

  // ─── Interactive: face scans ──────────────────────────────────────────────
  {
    name: 'ai_face_scan',
    description: 'Open the AI face scan flow for the user (camera/upload → cropped photo → submit). Use this when the user wants personalised photo insights from a selfie.',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
    interactive: true,
    screen: FaceScanScreen as ScreenComponent<FaceScanInput, FaceScanResult>,
    onComplete: (r: FaceScanResult, ctx) => {
      ctx.jobs.registerJob({
        id: r.jobId,
        kind: r.kind,
        label: r.kind === 'premium' ? 'Premium face scan' : 'Face scan',
      });
    },
    shape: (r: FaceScanResult) => ({
      job_id: r.jobId,
      kind: r.kind,
      message: 'Face scan submitted. The user can view results when processing finishes.',
    }),
  },
  {
    name: 'ai_premium_face_scan',
    description: '[PREMIUM] Open the premium face scan flow (768px crop, more detailed photo insights). Only callable for users on a premium plan.',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
    requiresPlan: 'premium',
    interactive: true,
    screen: PremiumFaceScanScreen as ScreenComponent<PremiumFaceScanInput, PremiumFaceScanResult>,
    onComplete: (r: PremiumFaceScanResult, ctx) => {
      ctx.jobs.registerJob({ id: r.jobId, kind: 'premium', label: 'Premium face scan' });
    },
    shape: (r: PremiumFaceScanResult) => ({
      job_id: r.jobId,
      kind: 'premium',
      message: 'Premium face scan submitted. Results will appear when processing finishes.',
    }),
  },
  {
    name: 'ai_hair_analysis',
    description: '[PREMIUM] Open the hair analysis flow (A4 portrait crop, AI-powered hair type and scalp condition analysis). Only callable for users on a premium plan.',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
    requiresPlan: 'premium',
    interactive: true,
    screen: HairAnalysisScreen as ScreenComponent<HairAnalysisScreenInput, HairAnalysisScreenResult>,
    onComplete: (r: HairAnalysisScreenResult, ctx) => {
      ctx.jobs.registerJob({ id: r.jobId, kind: 'hair', label: 'Hair analysis' });
    },
    shape: (r: HairAnalysisScreenResult) => ({
      job_id: r.jobId,
      kind: 'hair',
      message: 'Hair analysis submitted. Results will appear when processing finishes.',
    }),
  },
  {
    name: 'ai_makeup_analysis',
    description: '[PREMIUM] Open the makeup analysis flow (A4 portrait crop, AI-powered undertone and makeup palette analysis). Only callable for users on a premium plan.',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
    requiresPlan: 'premium',
    interactive: true,
    screen: MakeupAnalysisScreen as ScreenComponent<MakeupAnalysisScreenInput, MakeupAnalysisScreenResult>,
    onComplete: (r: MakeupAnalysisScreenResult, ctx) => {
      ctx.jobs.registerJob({ id: r.jobId, kind: 'makeup', label: 'Makeup analysis' });
    },
    shape: (r: MakeupAnalysisScreenResult) => ({
      job_id: r.jobId,
      kind: 'makeup',
      message: 'Makeup analysis submitted. Results will appear when processing finishes.',
    }),
  },

  // ─── Interactive: product flows ───────────────────────────────────────────
  {
    name: 'product_solo_analysis',
    description: 'Open the single-product analysis flow (barcode scan or manual entry). Returns product_id when the user submits.',
    parameters: {
      type: 'object',
      properties: {
        barcode: { type: 'string', description: 'Optional pre-filled barcode (8–14 digits).' },
      },
      required: [],
    },
    interactive: true,
    screen: ProductSoloScreen as ScreenComponent<ProductSoloInput, ProductSoloResult>,
    shape: (r: ProductSoloResult) => ({ product_id: r.product_id, barcode: r.barcode, source: r.source }),
  },
  {
    name: 'product_compare',
    description: 'Open the product-comparison flow (two barcodes side-by-side). Returns the comparison summary when the user submits.',
    parameters: {
      type: 'object',
      properties: {
        barcode_a: { type: 'string', description: 'Optional pre-filled barcode for product A.' },
        barcode_b: { type: 'string', description: 'Optional pre-filled barcode for product B.' },
      },
      required: [],
    },
    interactive: true,
    screen: ProductCompareScreen as ScreenComponent<ProductCompareInput, ProductCompareResult>,
    shape: (r: ProductCompareResult) => r.response,
  },
  {
    name: 'product_compatibility',
    description: 'Open the product-compatibility checker for two products with optional usage context (leave-on / leave-off).',
    parameters: {
      type: 'object',
      properties: {
        barcode_a: { type: 'string', description: 'Optional pre-filled barcode for product A.' },
        barcode_b: { type: 'string', description: 'Optional pre-filled barcode for product B.' },
      },
      required: [],
    },
    interactive: true,
    screen: ProductCompatScreen as ScreenComponent<ProductCompatInput, ProductCompatResult>,
    shape: (r: ProductCompatResult) => r.response,
  },

  // ─── Interactive: weather ─────────────────────────────────────────────────
  {
    name: 'weather_rating',
    description: 'Open the weather/UV/AQI skin analyzer for the user\'s current location. Returns a Skin Weather Score (0-100, higher = better for skin), a per-factor breakdown (UV, pollution, hydration, temperature, wind), and prioritized recommendations.',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
    interactive: true,
    screen: WeatherScreen as ScreenComponent<WeatherInput, WeatherResult>,
    shape: (r: WeatherResult) => r,
  },

  // ─── Interactive: preferences ─────────────────────────────────────────────
  {
    name: 'update_user_preferences',
    description: 'Open the preferences editor (avoid-list, sensitivity, ethical mode, breakout / gentle-actives flags) for the scanner and compatibility engines. The user reviews and saves.',
    parameters: {
      type: 'object',
      properties: {
        focus: {
          type: 'string',
          description: 'Which prefs tab to open first.',
          enum: ['scanner', 'compat'],
        },
      },
      required: [],
    },
    interactive: true,
    screen: PreferencesScreen as ScreenComponent<PreferencesInput, PreferencesResult>,
    onComplete: async (_r, ctx) => { await ctx.refreshPrefs(); },
    shape: (r: PreferencesResult) => ({
      ok: true,
      message: 'Preferences saved. New values will be visible in the next dynamic context.',
      saved: { scanner: !!r.scanner, compat: !!r.compat },
    }),
  },

  // ─── Non-interactive: data lookups ────────────────────────────────────────
  {
    name: 'get_user_history',
    description: 'Fetch the user\'s past skin analyses and scans. Defaults to the most recent page.',
    parameters: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max items to return (default 10, max 50).' },
        page: { type: 'number', description: 'Page index, 1-based (default 1).' },
      },
      required: [],
    },
    interactive: false,
    handler: async (args) => {
      const limit = Math.min(50, Math.max(1, argNumber(args.limit) ?? 10));
      const page = Math.max(1, argNumber(args.page) ?? 1);
      const res = await getHistory({ limit, page });
      return { count: res.items.length, items: res.items };
    },
  },
  {
    name: 'get_user_favorites',
    description: 'Fetch the user\'s favorited products.',
    parameters: {
      type: 'object',
      properties: {
        limit: { type: 'number', description: 'Max items (default 20, max 100).' },
        offset: { type: 'number', description: 'Pagination offset (default 0).' },
      },
      required: [],
    },
    interactive: false,
    handler: async (args) => {
      const limit = Math.min(100, Math.max(1, argNumber(args.limit) ?? 20));
      const offset = Math.max(0, argNumber(args.offset) ?? 0);
      return await listFavoriteProducts({ limit, offset });
    },
  },
  {
    name: 'get_product_info',
    description: 'Look up a product in the database by its numeric id. Returns ingredients and metadata.',
    parameters: {
      type: 'object',
      properties: {
        product_id: { type: 'number', description: 'The numeric product id.' },
      },
      required: ['product_id'],
    },
    interactive: false,
    handler: async (args) => {
      const id = argNumber(args.product_id);
      if (id === undefined) return { error: 'product_id must be a number' };
      return await getProductView(id);
    },
  },
  {
    name: 'get_ingredient_info',
    description: 'Look up an ingredient in the database. Accepts either a numeric id or a name (which will be resolved against listIngredients).',
    parameters: {
      type: 'object',
      properties: {
        ingredient_id: { type: 'number', description: 'Optional numeric ingredient id.' },
        name: { type: 'string', description: 'Optional ingredient name (e.g. "niacinamide"). Used when ingredient_id is not provided.' },
      },
      required: [],
    },
    interactive: false,
    handler: async (args) => {
      const id = argNumber(args.ingredient_id);
      if (id !== undefined) return await getIngredient(id);
      const name = argString(args.name);
      if (!name) return { error: 'Provide ingredient_id or name' };
      const list = await listIngredients({ name, limit: 1 });
      if (list.items.length === 0) return { error: `No ingredient found for "${name}"` };
      const first = list.items[0];
      return await getIngredient(first.id);
    },
  },
  {
    name: 'get_products_by_keyword',
    description: 'Search the product database by free-text keyword(s). Useful for resolving "the CeraVe SA cleanser" to candidate products.',
    parameters: {
      type: 'object',
      properties: {
        keywords: { type: 'string', description: 'Search keywords (e.g. "cerave sa cleanser").' },
        limit: { type: 'number', description: 'Max results (default 10, max 25).' },
      },
      required: ['keywords'],
    },
    interactive: false,
    handler: async (args) => {
      const keywords = argString(args.keywords);
      if (!keywords) return { error: 'keywords required' };
      const limit = Math.min(25, Math.max(1, argNumber(args.limit) ?? 10));
      const items = await fetchSearchProducts({ product_keywords: keywords, limit });
      return { count: items.length, items };
    },
  },
  {
    name: 'get_job_status',
    description: 'Fetch the live status of a single background job (face scan, premium scan, hair analysis, or makeup analysis) by id.',
    parameters: {
      type: 'object',
      properties: {
        job_id: { type: 'string', description: 'The job id.' },
        kind: { type: 'string', description: 'Job kind.', enum: ['base', 'premium', 'hair', 'makeup'] },
      },
      required: ['job_id'],
    },
    interactive: false,
    handler: async (args) => {
      const id = argString(args.job_id);
      if (!id) return { error: 'job_id required' };
      const kind = (argString(args.kind) || 'base') as 'base' | 'premium' | 'hair' | 'makeup';
      try {
        if (kind === 'premium') {
          const r = await getPremiumJob(id);
          return { id: r.job.id, kind, status: r.job.status, completed_at: r.job.completed_at ?? null };
        }
        if (kind === 'hair') {
          const r = await getHairAnalysisJob(id);
          return { id: r.job.id, kind, status: r.job.status, completed_at: r.job.completed_at ?? null };
        }
        if (kind === 'makeup') {
          const r = await getMakeupAnalysisJob(id);
          return { id: r.job.id, kind, status: r.job.status, completed_at: r.job.completed_at ?? null };
        }
        const r = await getJob(id);
        return { id: r.id, kind: 'base', status: r.status, submitted_at: r.submitted_at };
      } catch (error: unknown) {
        return { error: formatUnknownError(error, 'Failed to fetch job') };
      }
    },
  },
  {
    name: 'get_hair_analysis',
    description: 'Fetch the full results of a completed hair analysis job. Returns hair type, scalp condition, AI-generated result image, and personalised care recommendations.',
    parameters: {
      type: 'object',
      properties: {
        job_id: { type: 'string', description: 'The completed hair analysis job id (from get_user_history or after ai_hair_analysis).' },
      },
      required: ['job_id'],
    },
    interactive: false,
    handler: async (args) => {
      const id = argString(args.job_id);
      if (!id) return { error: 'job_id required' };
      try {
        return await getHairAnalysisJob(id);
      } catch (error: unknown) {
        return { error: formatUnknownError(error, 'Failed to fetch hair analysis results') };
      }
    },
  },
  {
    name: 'get_makeup_analysis',
    description: 'Fetch the full results of a completed makeup analysis job. Returns undertone, colour palette, product recommendations, and AI-generated result image.',
    parameters: {
      type: 'object',
      properties: {
        job_id: { type: 'string', description: 'The completed makeup analysis job id (from get_user_history or after ai_makeup_analysis).' },
      },
      required: ['job_id'],
    },
    interactive: false,
    handler: async (args) => {
      const id = argString(args.job_id);
      if (!id) return { error: 'job_id required' };
      try {
        return await getMakeupAnalysisJob(id);
      } catch (error: unknown) {
        return { error: formatUnknownError(error, 'Failed to fetch makeup analysis results') };
      }
    },
  },
  {
    name: 'remove_job_from_memory',
    description: 'Remove a job from the active-jobs list shown in your dynamic context. Call this when the user has viewed the result and the job no longer needs tracking.',
    parameters: {
      type: 'object',
      properties: {
        job_id: { type: 'string', description: 'The job id to drop.' },
      },
      required: ['job_id'],
    },
    interactive: false,
    handler: (args, ctx) => {
      const id = argString(args.job_id);
      if (!id) return Promise.resolve({ error: 'job_id required' });
      const wasTracked = ctx.jobs.isTracked(id);
      ctx.jobs.markViewed(id);
      return Promise.resolve({ ok: true, removed: wasTracked, id });
    },
  },
];

/** Tools indexed by name for O(1) dispatch lookup. */
export const TOOL_INDEX: Record<string, ToolDef> = TOOL_REGISTRY.reduce<Record<string, ToolDef>>((acc, t) => {
  acc[t.name] = t;
  return acc;
}, {});

/** Compact one-line description used to keep the system prompt small. */
function shortDesc(t: ToolDef): string {
  const first = t.description.split(/[.!?]\s/)[0]; // first sentence only
  return first.length > 120 ? `${first.slice(0, 117)}…` : first;
}

function paramSummary(t: ToolDef): string {
  const props = t.parameters.properties;
  const required = new Set(t.parameters.required);
  const parts = Object.entries(props).map(([k, p]) => {
    const req = required.has(k) ? '' : '?';
    if (p.enum?.length) return `${k}${req}: ${p.enum.join('|')}`;
    return `${k}${req}: ${p.type}`;
  });
  return parts.length > 0 ? `(${parts.join(', ')})` : '()';
}

/**
 * Tools whose required parameters are identifiers the user does NOT know
 * (job_id, product_id, ingredient_id, …) — these must be chained from another
 * tool's result, never asked of the user directly. We surface the chain
 * source inline so a small model has no excuse to leak the identifier name.
 */
const CHAIN_HINTS: Record<string, string> = {
  analyze_skin: 'chain from get_user_history (pick newest completed scan)',
  get_product_info: 'chain from get_products_by_keyword or get_user_favorites',
  get_ingredient_info: 'use the ingredient name directly when possible; otherwise no chain needed',
  get_job_status: 'chain from active_jobs in <context>, never ask the user',
  remove_job_from_memory: 'use the job id from active_jobs in <context>',
  get_hair_analysis: 'chain from get_user_history (pick newest completed hair scan)',
  get_makeup_analysis: 'chain from get_user_history (pick newest completed makeup scan)',
};

/**
 * Prompt-friendly tool surface — one line per tool, with explicit chain hints
 * for the tools whose required params are identifiers the user can't know.
 */
export function serializeToolsForPrompt(): string {
  return TOOL_REGISTRY
    .map((t) => {
      const planTag = t.requiresPlan === 'premium' ? ' [PREMIUM]' : '';
      const chainTag = CHAIN_HINTS[t.name] ? ` [CHAIN: ${CHAIN_HINTS[t.name]}]` : '';
      return `- ${t.name}${paramSummary(t)}${planTag}${chainTag}: ${shortDesc(t)}`;
    })
    .join('\n');
}
